import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';
import {
  Table, Button, Input, Form, message, Space, Row, Col, Modal, Tag, DatePicker, Select,
  Card, Statistic, Typography, Tooltip, Popconfirm
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlusOutlined,
  EyeOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { fetchWithLoading } from '../utils/fetchHelper';
import { MaintenanceCampaign } from '../types';
import dayjs from 'dayjs';


const { RangePicker } = DatePicker;
const { Title } = Typography;

interface MaintenanceCampaignFormProps {
  form: any;
  initialValues?: Partial<MaintenanceCampaign>;
  onFinish: (values: any) => void;
  loading: boolean;
  submitText: string;
  companies: any[];
  locations: any[];
  employees: any[];
}

const MaintenanceCampaignForm: React.FC<MaintenanceCampaignFormProps> = ({
  form,
  initialValues,
  onFinish,
  loading,
  submitText,
  companies,
  locations,
  employees
}) => {
  // Log để debug
  React.useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      console.log('🔍 Form initialValues:', initialValues);
      console.log('   - conducted_by_employee_id:', initialValues.conducted_by_employee_id);
      console.log('   - company_id:', initialValues.company_id);
    }
  }, [initialValues]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={initialValues}
    >
      <Form.Item
        label="Tên đợt tác nghiệp định kỳ"
        name="name"
        rules={[{ required: true, message: 'Vui lòng nhập tên đợt tác nghiệp định kỳ' }]}
      >
        <Input placeholder="Nhập tên đợt tác nghiệp định kỳ" />
      </Form.Item>

      <Form.Item
        label="Thời gian thực hiện"
        name="date_range"
        rules={[{ required: true, message: 'Vui lòng chọn thời gian thực hiện' }]}
      >
        <RangePicker
          style={{ width: '100%' }}
          format="DD/MM/YYYY"
          placeholder={['Ngày bắt đầu', 'Ngày kết thúc']}
        />
      </Form.Item>

      <Form.Item
        label="Công ty"
        name="company_id"
        rules={[{ required: true, message: 'Vui lòng chọn công ty' }]}
      >
        <Select
          placeholder="Chọn công ty"
          options={companies.map(company => ({ value: company.id, label: company.name }))}
          onChange={(value) => {
            console.log('🔍 Company selected:', value);
            console.log('   - Available employees for company:', employees.filter(emp => emp.company_id === value));
            // Reset người phụ trách khi thay đổi công ty
            form.setFieldValue('conducted_by_employee_id', undefined);
          }}
        />
      </Form.Item>

      <Form.Item
        label="Địa điểm"
        name="location_id"
      >
        <Select
          placeholder="Chọn địa điểm (tùy chọn)"
          allowClear
          options={locations.map(location => ({ value: location.id, label: location.name }))}
        />
      </Form.Item>

      <Form.Item
        label="Người phụ trách"
        name="conducted_by_employee_id"
        rules={[{ required: true, message: 'Vui lòng chọn người phụ trách' }]}
      >
        <Select
          placeholder="Chọn người phụ trách"
          options={employees
            .filter((emp: any) => {
              // Lọc employees theo công ty được chọn
              const selectedCompanyId = form.getFieldValue('company_id');
              console.log('🔍 Filtering employees:', {
                selectedCompanyId,
                totalEmployees: employees.length,
                employeeCompanyIds: employees.map(e => ({ id: e.id, company_id: e.company_id, name: e.full_name }))
              });

              if (!selectedCompanyId) return true; // Hiển thị tất cả nếu chưa chọn công ty

              // Chỉ hiển thị employees thuộc công ty được chọn
              const filtered = emp.company_id === selectedCompanyId;
              console.log(`   - Employee ${emp.full_name} (ID: ${emp.id}): company_id=${emp.company_id}, selected=${selectedCompanyId}, filtered=${filtered}`);
              return filtered;
            })
            .map((emp: any) => ({
              value: emp.id,
              label: emp.full_name
            }))}
          onChange={(value) => {
            console.log('🔍 Employee selected:', value);
            const selectedEmployee = employees.find(emp => emp.id === value);
            console.log('   - Selected employee details:', selectedEmployee);
          }}
        />
      </Form.Item>

      <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" loading={loading}>
          {submitText}
        </Button>
      </Form.Item>
    </Form>
  );
};

const MaintenanceCampaignPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [campaigns, setCampaigns] = useState<MaintenanceCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<MaintenanceCampaign | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // New states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [companyFilter, setCompanyFilter] = useState<number | null>(null);
  const [locationFilter, setLocationFilter] = useState<number | null>(null);

  // Helper functions
  const getCurrentUser = (): any => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  };

  const fetchCampaigns = async () => {
    await fetchWithLoading(
      () => apiClient.getMaintenanceCampaigns(),
      setLoading,
      (data) => setCampaigns(data as MaintenanceCampaign[]),
      'Không thể tải danh sách đợt tác nghiệp định kỳ'
    );
  };

  const fetchMappingData = async () => {
    try {
      console.log('🔍 Fetching mapping data...');
      const [com, loc, emp] = await Promise.all([
        apiClient.getCompanies(),
        apiClient.getLocations(),
        apiClient.getEmployees(),
      ]);

      console.log('📊 Mapping data received:', {
        companies: Array.isArray(com) ? com.length : 'not array',
        locations: Array.isArray(loc) ? loc.length : 'not array',
        employees: Array.isArray(emp) ? emp.length : 'not array'
      });

      if (Array.isArray(emp)) {
        console.log('👥 Employees details:', emp.map(e => ({ id: e.id, name: e.full_name, company_id: e.company_id })));
      }

      setCompanies(Array.isArray(com) ? com : []);
      setLocations(Array.isArray(loc) ? loc : []);
      setEmployees(Array.isArray(emp) ? emp : []);
    } catch (err) {
      console.error('❌ Error fetching mapping data:', err);
      message.error('Không thể tải dữ liệu tham chiếu');
    }
  };



  const handleViewDetail = (campaign: MaintenanceCampaign) => {
    navigate(`/maintenance-campaigns/${campaign.id}`);
  };







  useEffect(() => {
    fetchCampaigns();
    fetchMappingData();
  }, []);

  useEffect(() => {
    const user = getCurrentUser();
    const isSuperAdminUser = user.role === 'super_admin';
    setIsSuperAdmin(isSuperAdminUser);
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      console.log('🔍 Form values nhận được:', values);
      console.log('   - conducted_by_employee_id:', values.conducted_by_employee_id);
      console.log('   - typeof:', typeof values.conducted_by_employee_id);

      const campaignData = {
        ...values,
        start_date: values.date_range[0].format('YYYY-MM-DD'),
        end_date: values.date_range[1].format('YYYY-MM-DD'),
        // Gửi trực tiếp conducted_by_employee_id, để Backend xử lý mapping
        conducted_by_employee_id: values.conducted_by_employee_id
      };
      delete campaignData.date_range;

      console.log('📤 Campaign data sẽ gửi:', campaignData);
      console.log('   - Final conducted_by_employee_id:', campaignData.conducted_by_employee_id);

      if (editingCampaign) {
        await apiClient.updateMaintenanceCampaign(editingCampaign.id, campaignData);
        message.success('Cập nhật đợt tác nghiệp định kỳ thành công!');
      } else {
        await apiClient.createMaintenanceCampaign(campaignData);
        message.success('Tạo đợt tác nghiệp định kỳ thành công!');
      }
      form.resetFields();
      setEditingCampaign(null);
      setModalVisible(false);
      fetchCampaigns();
    } catch (err: any) {
      console.error('❌ Lỗi khi submit:', err);
      console.error('   - Response data:', err?.response?.data);
      const msg = err?.response?.data?.message || (editingCampaign ? 'Cập nhật thất bại!' : 'Tạo đợt tác nghiệp định kỳ thất bại!');
      message.error(msg);
    }
  };

  const handleEdit = (record: MaintenanceCampaign) => {
    // Kiểm tra trạng thái trước khi cho phép sửa
    if (record.status === 'approved') {
      message.warning('Không thể chỉnh sửa đợt tác nghiệp đã hoàn thành!');
      return;
    }

    console.log('🔍 Edit campaign:', record);
    console.log('   - conducted_by_employee_id:', record.conducted_by_employee_id);
    console.log('   - company_id:', record.company_id);

    setEditingCampaign(record);

    // Sử dụng trực tiếp conducted_by_employee_id từ record
    const formValues = {
      name: record.name,
      date_range: [dayjs(record.start_date), dayjs(record.end_date)],
      company_id: record.company_id,
      location_id: record.location_id,
      conducted_by_employee_id: record.conducted_by_employee_id
    };

    console.log('   - Form values sẽ set:', formValues);

    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteMaintenanceCampaign(id);
      message.success('Đã xóa đợt tác nghiệp định kỳ');
      fetchCampaigns();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Xóa đợt tác nghiệp định kỳ thất bại!';
      message.error(errorMessage);
    }
  };

  const handleDeleteClick = (record: MaintenanceCampaign) => {
    // Kiểm tra trạng thái trước khi cho phép xóa
    if (record.status === 'approved') {
      message.warning('Không thể xóa đợt tác nghiệp đã hoàn thành!');
      return;
    }

    // Kiểm tra quyền super admin
    if (!isSuperAdmin) {
      message.warning('Bạn không có quyền xóa đợt tác nghiệp định kỳ!');
      return;
    }

    handleDelete(record.id);
  };

  const handleStartCampaign = async (id: number) => {
    try {
      await apiClient.startMaintenanceCampaign(id);
      message.success('Đã bắt đầu đợt tác nghiệp định kỳ');
      fetchCampaigns();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Không thể bắt đầu đợt tác nghiệp định kỳ');
    }
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_execution_approval': return 'orange';
      case 'in_progress': return 'processing';
      case 'pending_completion_approval': return 'purple';
      case 'approved': return 'success';
      case 'rejected_editable': return 'warning';
      case 'rejected_final': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending_execution_approval': return 'Chờ duyệt thực hiện';
      case 'in_progress': return 'Đang thực hiện';
      case 'pending_completion_approval': return 'Chờ duyệt hoàn thành';
      case 'approved': return 'Đã duyệt (hoàn thành)';
      case 'rejected_editable': return 'Từ chối (có thể sửa)';
      case 'rejected_final': return 'Từ chối (cuối cùng)';
      default: return status;
    }
  };

  // Statistics calculation
  const statistics = useMemo(() => {
    const total = campaigns.length;
    const byStatus = campaigns.reduce((acc, campaign) => {
      acc[campaign.status] = (acc[campaign.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const inProgress = byStatus.in_progress || 0;
    const pendingExecutionApproval = byStatus.pending_execution_approval || 0;
    const pendingCompletionApproval = byStatus.pending_completion_approval || 0;
    const approved = byStatus.approved || 0;

    return {
      total,
      inProgress,
      pendingExecutionApproval,
      pendingCompletionApproval,
      approved,
      completionRate: total > 0 ? Math.round((approved / total) * 100) : 0
    };
  }, [campaigns]);

  // Filtered campaigns
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      const matchesSearch = c.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        c.company_name?.toLowerCase().includes(searchValue.toLowerCase());
      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchesCompany = !companyFilter || c.company_id === companyFilter;
      const matchesLocation = !locationFilter || c.location_id === locationFilter;

      return matchesSearch && matchesStatus && matchesCompany && matchesLocation;
    });
  }, [campaigns, searchValue, statusFilter, companyFilter, locationFilter]);

  const columns = [
    {
      title: 'Tên đợt tác nghiệp',
      dataIndex: 'name',
      key: 'name',
      align: 'left' as const,
      ellipsis: true,
      render: (text: string, record: MaintenanceCampaign) => (
        <div>
          <strong>{text}</strong>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ID: #{record.id}
          </div>
        </div>
      )
    },
    {
      title: 'Công ty',
      dataIndex: 'company_name',
      key: 'company_name',
      align: 'center' as const,
      ellipsis: true
    },
    {
      title: 'Thời gian',
      key: 'date_range',
      align: 'center' as const,
      width: 140,
      render: (record: MaintenanceCampaign) => (
        <div>
          <div>Từ: {dayjs(record.start_date).format('DD/MM/YYYY')}</div>
          <div>Đến: {dayjs(record.end_date).format('DD/MM/YYYY')}</div>
        </div>
      )
    },
    {
      title: 'Vị trí',
      key: 'location_name',
      align: 'center' as const,
      render: (record: MaintenanceCampaign) => {
        const primaryLocation = record.location_name;
        const allLocations = record.all_location_names;

        if (!allLocations || allLocations === primaryLocation) {
          return primaryLocation || '—';
        }

        return (
          <Tooltip title={`Đợt tác nghiệp này bao gồm tài sản tại các vị trí: ${allLocations}`}>
            <span style={{ cursor: 'help', borderBottom: '1px dotted #ccc' }}>
              {primaryLocation || 'Nhiều vị trí'}*
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      width: 160,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'Người phụ trách',
      dataIndex: 'conducted_by_name',
      key: 'conducted_by_name',
      align: 'center' as const,
      ellipsis: true
    },
    {
      title: 'Hành động',
      key: 'action',
      align: 'center' as const,
      width: 120,
      render: (_: any, record: MaintenanceCampaign) => {
        const isCompleted = record.status === 'approved';

        return (
          <Space>
            <Tooltip title="Xem chi tiết">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            {!isCompleted && getCurrentUser()?.role !== 'employee_user' && (
              <Tooltip title="Chỉnh sửa">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            )}
            {!isCompleted && isSuperAdmin && (
              <Popconfirm
                title="Bạn có chắc muốn xóa đợt tác nghiệp định kỳ này?"
                onConfirm={() => handleDeleteClick(record)}
                okText="Xóa"
                cancelText="Hủy"
              >
                <Tooltip title="Xóa đợt tác nghiệp">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  ];



  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'inline-block' }}>
          Tác nghiệp định kỳ
        </Title>
        {getCurrentUser()?.role !== 'employee_user' && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/maintenance-campaigns/create')}
            style={{ float: 'right' }}
            size="large"
          >
            Tạo đợt tác nghiệp
          </Button>
        )}
      </div>

      {/* Thống kê - giống trang kiểm kê */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Tổng đợt tác nghiệp" value={statistics.total} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Đang thực hiện" value={statistics.inProgress} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Chờ duyệt thực hiện" value={statistics.pendingExecutionApproval} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Chờ duyệt hoàn thành" value={statistics.pendingCompletionApproval} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      {/* Filter section - giống trang kiểm kê */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Input
              placeholder="Tìm kiếm đợt tác nghiệp định kỳ"
              value={searchValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Trạng thái"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              options={[
                { value: 'all', label: 'Tất cả trạng thái' },
                { value: 'pending_execution_approval', label: 'Chờ duyệt thực hiện' },
                { value: 'in_progress', label: 'Đang tiến hành' },
                { value: 'pending_completion_approval', label: 'Chờ duyệt hoàn thành' },
                { value: 'approved', label: 'Đã duyệt (Hoàn thành)' },
                { value: 'rejected_editable', label: 'Từ chối (có thể sửa)' },
                { value: 'rejected_final', label: 'Từ chối (cuối cùng)' }
              ]}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Công ty"
              value={companyFilter}
              onChange={(value) => {
                setCompanyFilter(value);
                setLocationFilter(null);
              }}
              allowClear
              style={{ width: '100%' }}
              options={companies.map(company => ({
                value: company.id,
                label: company.name
              }))}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Địa điểm"
              value={locationFilter}
              onChange={setLocationFilter}
              allowClear
              style={{ width: '100%' }}
              options={locations
                .filter(loc => !companyFilter || loc.company_id === companyFilter)
                .map(location => ({
                  value: location.id,
                  label: location.name
                }))}
            />
          </Col>
          <Col span={4}>
            <Button
              icon={<FileExcelOutlined />}
              onClick={async () => {
                try {
                  if (!editingCampaign?.id) {
                    message.info('Chọn một đợt tác nghiệp để xuất Excel.');
                    return;
                  }
                  const blob = await apiClient.exportMaintenanceCampaignExcel(Number(editingCampaign.id));
                  const link = document.createElement('a');
                  link.href = window.URL.createObjectURL(blob);
                  link.download = `work_records_campaign_${editingCampaign.id}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  message.success('Đã xuất Excel');
                } catch (err: any) {
                  console.error(err);
                  message.error(err?.message || 'Không thể xuất Excel');
                }
              }}
            >
              Xuất Excel
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Existing modals */}
      <Modal
        open={modalVisible}
        title={editingCampaign ? 'Cập nhật đợt tác nghiệp định kỳ' : 'Tạo mới đợt tác nghiệp định kỳ'}
        onCancel={() => {
          setModalVisible(false);
          setEditingCampaign(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <MaintenanceCampaignForm
          form={form}
          initialValues={editingCampaign || {}}
          onFinish={handleSubmit}
          loading={loading}
          submitText={editingCampaign ? 'Cập nhật' : 'Tạo mới'}
          companies={companies}
          locations={locations}
          employees={employees}
        />
        {editingCampaign && (
          <Button
            className="ams-btn"
            style={{ width: '100%', borderRadius: 8, height: 40, marginTop: 8 }}
            size="large"
            onClick={() => {
              setEditingCampaign(null);
              form.resetFields();
              setModalVisible(false);
            }}
          >
            Hủy
          </Button>
        )}
      </Modal>





      {/* Table */}
      <Card title={`Danh sách đợt tác nghiệp định kỳ (${statistics.total})`}>
        <Table
          dataSource={filteredCampaigns}
          rowKey="id"
          loading={loading}
          columns={columns}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} đợt tác nghiệp định kỳ`,
          }}
        />
      </Card>
    </div>
  );
};

export default MaintenanceCampaignPage; 